import React, { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useShopifyVariantLookup, prefetchShopifyVariants } from '../../hooks/useShopifyVariantLookup';
import styles from './styles.module.scss';

interface CartItem {
  variant_id: number;
  id?: number;
  title?: string;
  variant_title?: string;
  quantity?: number;
  price?: string;
  line_price?: string;
  properties?: Record<string, string>;
}

interface CartData {
  items: CartItem[];
  item_count?: number;
  total_price?: string;
}

const DevCartOverlay: React.FC = () => {
  const [cart, setCart] = React.useState<CartData | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const queryClient = useQueryClient();

  // Prefetch all unknown variants when cart loads
  useEffect(() => {
    if (cart?.items) {
      const unknownVariantIds = cart.items
        .filter(item => !item.title || !item.variant_title)
        .map(item => item.variant_id || item.id)
        .filter(Boolean);

      if (unknownVariantIds.length > 0) {
        prefetchShopifyVariants(unknownVariantIds, queryClient);
      }
    }
  }, [cart, queryClient]);

  // Fetch current cart
  const fetchCart = React.useCallback(async () => {
    // try {
    //   const storeUrl = import.meta.env.VITE_SHOPIFY_STORE_URL || window.location.origin;
    //   const response = await fetch(`${storeUrl}/cart.js`);
    //   if (response.ok) {
    //     const cartData = await response.json();
    //     setCart(cartData);
    //   }
    // } catch (err) {
    //   console.error('Failed to fetch cart:', err);
    // }
  }, []);

  useEffect(() => {
    //fetchCart();

    // Auto refresh cart every 10 seconds
    const interval = setInterval(fetchCart, 10000);
    return () => clearInterval(interval);
  }, [fetchCart]);

  // Toggle with keyboard shortcut (Ctrl + Shift + C)
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'C') {
        setIsOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, []);

  if (!isOpen || process.env.NODE_ENV !== 'development') return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.header}>
        <h3>Dev Cart Debug</h3>
        <button className={styles.closeBtn} onClick={() => setIsOpen(false)}>×</button>
        <button className={styles.refreshBtn} onClick={fetchCart}>↻ Refresh</button>
      </div>

      {!cart ? (
        <div className={styles.loading}>Loading cart...</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Variant ID</th>
              <th>Product</th>
              <th>Variant</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Properties</th>
            </tr>
          </thead>
          <tbody>
            {cart.items.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.empty}>Cart is empty</td>
              </tr>
            ) : (
              cart.items.map((item, index) => (
                <CartRow key={item.variant_id || item.id || index} item={item} index={index} />
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

// Individual cart row with lookup
const CartRow: React.FC<{ item: CartItem; index: number }> = ({ item, index }) => {
  const variantId = item.variant_id || item.id;
  const { data, isLoading, isError } = useShopifyVariantLookup(variantId);

  // Use existing title first, fall back to fetched data
  const productTitle = useMemo(() => {
    if (item.title) return item.title;
    if (data?.productTitle) return data.productTitle;
    if (isLoading) return <span className={styles.loadingText}>Loading...</span>;
    if (isError) return <span className={styles.errorText}>Failed to load</span>;
    return <span className={styles.unknown}>Unknown</span>;
  }, [item.title, data, isLoading, isError]);

  const variantTitle = useMemo(() => {
    if (item.variant_title) return item.variant_title;
    if (data?.variantTitle) return data.variantTitle;
    if (isLoading) return <span className={styles.loadingText}>Loading...</span>;
    if (isError) return <span className={styles.errorText}>Failed to load</span>;
    return <span className={styles.unknown}>Unknown</span>;
  }, [item.variant_title, data, isLoading, isError]);

  return (
    <tr className={isLoading ? styles.loadingRow : ''}>
      <td>{index + 1}</td>
      <td className={styles.variantId}>{variantId}</td>
      <td className={styles.titleCell}>{productTitle}</td>
      <td className={styles.variantCell}>{variantTitle}</td>
      <td>{item.quantity}</td>
      <td data-testid={`dev-cart-item-price-${variantId}`}>${(parseFloat(item.price || '0') / 100).toFixed(2)}</td>
      <td className={styles.properties}>
        {item.properties && Object.keys(item.properties).length > 0 ? (
          <pre>{JSON.stringify(item.properties, null, 2)}</pre>
        ) : (
          <span className={styles.noProps}>—</span>
        )}
      </td>
    </tr>
  );
};

export default DevCartOverlay;
